import { Children, createContext, useContext, useRef, ReactNode, useCallback, useMemo, useState } from 'react'

/** Index of the current alert in the stack (0 = bottom). Set by provider per child. */
export const StackIndexContext = createContext<number | null>(null)

interface StackedAlertsContextValue {
  /** Register this alert's height by index. Call during render. */
  registerAlert: (index: number, height: number) => void
  /** Unregister this alert by index. Call on unmount. */
  unregisterAlert: (index: number) => void
  /** Get the bottom offset (px) for the given index. Depends on heights of lower indices. */
  getOffset: (index: number) => number
  /** Base offset */
  baseOffset: number
  /** Layout version - increments when heights change */
  layoutVersion: number
}

const StackedAlertsContext = createContext<StackedAlertsContextValue | null>(null)

/**
 * Hook for AlertContainer to check if it's inside a stacked context
 * and get its positioning if so.
 */
export const useStackedAlerts = () => {
  return useContext(StackedAlertsContext)
}

interface StackedAlertsProviderProps {
  children: ReactNode
  baseOffset?: number
  gap?: number
}

/**
 * Provider that manages stacked alert positioning.
 * Alerts inside this provider will be positioned by index: first child = index 0 (bottom),
 * subsequent children stack above. Offsets are computed from registered heights so
 * order of registration does not affect layout.
 */
export const StackedAlertsProvider = ({ children, baseOffset = 80, gap = 16 }: StackedAlertsProviderProps) => {
  const heightsRef = useRef<Record<number, number>>({})
  const [layoutVersion, setLayoutVersion] = useState(0)

  const registerAlert = useCallback((index: number, height: number) => {
    if (heightsRef.current[index] !== height) {
      heightsRef.current = { ...heightsRef.current, [index]: height }
      setLayoutVersion(v => v + 1)
    }
  }, [])

  const unregisterAlert = useCallback((index: number) => {
    const newHeights = { ...heightsRef.current }
    if (newHeights[index] !== undefined) {
      delete newHeights[index]
      heightsRef.current = newHeights
      setLayoutVersion(v => v + 1)
    }
  }, [])

  const getOffset = useCallback(
    (index: number): number => {
      let offset = baseOffset
      for (let i = 0; i < index; i++) {
        offset += (heightsRef.current[i] ?? 0) + gap
      }
      return offset
    },
    [baseOffset, gap]
  )

  const value = useMemo(
    () => ({ registerAlert, unregisterAlert, getOffset, baseOffset, layoutVersion }),
    [registerAlert, unregisterAlert, getOffset, baseOffset, layoutVersion]
  )

  return (
    <StackedAlertsContext.Provider value={value}>
      {Children.map(children, (child, index) => (
        <StackIndexContext.Provider key={index} value={index}>
          {child}
        </StackIndexContext.Provider>
      ))}
    </StackedAlertsContext.Provider>
  )
}

export default StackedAlertsContext
